import { useState } from "react";
import ReadMoreModal from "./ReadMoreModal";

/**
 * FixedCard — uniform-height card used across the whole site.
 *
 * Props:
 *   height       — total card height in px (default 340)
 *   headerHeight — coloured top section height in px (default 150)
 *   accentColor  — header gradient start colour
 *   headerContent— JSX rendered inside the header (icon, tag, image…)
 *   eyebrow      — tiny ALL-CAPS label above title
 *   title        — main card title
 *   meta         — small grey line below title (client, date, etc.)
 *   body         — full description text
 *   previewLines — how many lines to show before truncating (default 3)
 *   modalTitle   — title in the read-more popup (defaults to `title`)
 *   modalSubtitle— subtitle in the popup (defaults to `eyebrow`)
 *   modalIcon    — emoji icon for popup header (optional)
 *   modalIsPhoto — treat modalIcon as img src
 *   extra        — JSX rendered at bottom of card (badges, links)
 *   modalExtra   — JSX rendered inside modal below body
 *   onClick      — optional click handler for the whole card
 */
export default function FixedCard({
  height       = 340,
  headerHeight = 150,
  accentColor  = "#8A1613",
  headerContent,
  eyebrow,
  title,
  meta,
  body         = "",
  previewLines = 3,
  modalTitle,
  modalSubtitle,
  modalIcon,
  modalIsPhoto = false,
  extra,
  modalExtra,
  onClick,
}) {
  const [showModal, setShowModal] = useState(false);

  const PREVIEW_CHARS = previewLines * 55; // rough chars-per-line estimate
  const isLong = body.length > PREVIEW_CHARS;
  const preview = isLong ? body.slice(0, PREVIEW_CHARS).trimEnd() + "…" : body;

  const handleClick = () => { if (onClick) onClick(); };

  return (
    <>
      <div
        onClick={handleClick}
        style={{
          background: "#fff",
          borderRadius: 20,
          overflow: "hidden",
          border: "1px solid rgba(11,11,11,0.07)",
          height: height,
          display: "flex",
          flexDirection: "column",
          cursor: onClick ? "pointer" : "default",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {/* ── Fixed-height coloured header ── */}
        <div style={{
          height: headerHeight, flexShrink: 0,
          background: `linear-gradient(135deg, ${accentColor} 0%, rgba(149,187,131,0.55) 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "hidden",
        }}>
          {headerContent}
          {/* Fade into white card body */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
            background: "linear-gradient(to top, #fff, transparent)",
          }} />
        </div>

        {/* ── Text content — fills remaining height, never overflows ── */}
        <div style={{
          flex: 1, padding: "1rem 1.4rem 1.25rem",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}>
          {eyebrow && (
            <p style={{
              fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.68rem",
              color: accentColor, letterSpacing: "0.1em", textTransform: "uppercase",
              marginBottom: "0.3rem", flexShrink: 0,
            }}>
              {eyebrow}
            </p>
          )}

          <h3 style={{
            fontFamily: "var(--font)", fontWeight: 800,
            fontSize: "1rem", color: "#0B0B0B",
            marginBottom: meta ? "0.25rem" : "0.6rem",
            lineHeight: 1.3, flexShrink: 0,
          }}>
            {title}
          </h3>

          {meta && (
            <p style={{
              fontFamily: "var(--font-body)", fontSize: "0.75rem",
              color: "rgba(11,11,11,0.38)", marginBottom: "0.5rem", flexShrink: 0,
            }}>
              {meta}
            </p>
          )}

          {/* Body preview — CSS line-clamp keeps it hard-capped */}
          {body && (
            <p style={{
              fontFamily: "var(--font-body)", fontSize: "0.86rem",
              color: "rgba(11,11,11,0.55)", lineHeight: 1.68,
              margin: 0, flex: 1,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: previewLines,
              WebkitBoxOrient: "vertical",
            }}>
              {preview}
            </p>
          )}

          {/* Bottom row: extra badges/links + optional Read more */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "flex-end", marginTop: "0.6rem", flexShrink: 0,
          }}>
            <div>{extra}</div>
            {isLong && (
              <button
                onClick={e => { e.stopPropagation(); setShowModal(true); }}
                style={{
                  background: "none", border: "none", padding: 0,
                  fontFamily: "var(--font)", fontWeight: 700,
                  fontSize: "0.78rem", color: accentColor,
                  cursor: "pointer", textDecoration: "underline",
                  textUnderlineOffset: "3px", flexShrink: 0,
                }}
              >
                Read more →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Read-more popup */}
      {showModal && (
        <ReadMoreModal
          title={modalTitle || title}
          subtitle={modalSubtitle || eyebrow}
          body={body}
          accentColor={accentColor}
          icon={modalIcon}
          isPhoto={modalIsPhoto}
          extra={modalExtra}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
